import type { TechnologyCategory, TechnologyRaw } from "db/custom/schema/technologies";

const CMS_BASE_URL = process.env.NEXT_PUBLIC_CMS_API_URL || 'http://localhost:4321';

export interface TechnologiesResponse {
    data: TechnologyRaw[];
    total: number;
}

export async function fetchTechnologies(): Promise<TechnologiesResponse> {
    try {
        const url = `${CMS_BASE_URL}/api/v1/technologies?limit=200&sort=familiarity:desc&sort=categoryOrder:asc`;
        //console.log('Fetching technologies from:', url);

        const response = await fetch(url, {
            headers: {
                'Content-Type': 'application/json',
            },
            // Enable caching for SSR
            next: {
                revalidate: 3600, // Revalidate every hour (as requested)
            },
        });

        if (!response.ok) throw new Error(`Failed to fetch technologies: ${response.status} ${response.statusText}`);

        const data = await response.json() as TechnologiesResponse;

        return {
            data: data.data || [],
            total: data.total || 0,
        };
    } catch (error) {
        console.error('Error fetching technologies:', error);
        throw error;
    }
}

// Query key factory for consistent key management
export const technologiesQueryKeys = {
    all: ['technologies'] as const,
    lists: () => [...technologiesQueryKeys.all, 'list'] as const,
    list: (filters?: Record<string, any>) => [...technologiesQueryKeys.lists(), { filters }] as const,
    details: () => [...technologiesQueryKeys.all, 'detail'] as const,
    detail: (id: string) => [...technologiesQueryKeys.details(), id] as const,
    byCategory: (category: TechnologyCategory) => [...technologiesQueryKeys.all, 'category', category] as const,
    highFamiliarity: () => [...technologiesQueryKeys.all, 'highFamiliarity'] as const,
    lowFamiliarity: () => [...technologiesQueryKeys.all, 'lowFamiliarity'] as const,
};

// Helper functions to process the data
export function parseExtraStyle(styleString: string | null): React.CSSProperties | undefined {
    if (!styleString) return undefined;
    try {
        return JSON.parse(styleString);
    } catch {
        return undefined;
    }
}

export function getHighFamiliarityTechnologies(technologies: ProcessedTechnology[]): ProcessedTechnology[] {
    return technologies.filter(tech => tech.familiarity >= 3);
}

export function getLowFamiliarityTechnologies(technologies: ProcessedTechnology[]): ProcessedTechnology[] {
    return technologies.filter(tech => tech.familiarity < 3);
}

export function getTechnologiesByCategory(technologies: ProcessedTechnology[], category: TechnologyCategory): ProcessedTechnology[] {
    return technologies.filter(tech => tech.category === category);
}

// Transform CMS technology data to match the original format
export interface TechnologyMark {
    wordmarkUrl?: string;
    extraWordmarkStyle?: React.CSSProperties;
    iconUrl?: string | null;
    extraIconStyle?: React.CSSProperties;
    textColor?: string;
    extraTextStyle?: React.CSSProperties;
}

export interface ProcessedTechnology {
    familiarity: 1 | 2 | 3 | 4 | 5;
    category: TechnologyCategory;
    backgroundColor: string;
    name: string;
    mark: TechnologyMark;
    noBorder?: boolean;
    description: string;
    id: string;
}

export function transformTechnologyForUI(tech: TechnologyRaw): ProcessedTechnology {
    const mark: TechnologyMark = {};

    if (tech.wordmark) {
        mark.wordmarkUrl = tech.wordmark;
        const extraWordmarkStyle = parseExtraStyle(tech.extraWordmarkStyle);
        if (extraWordmarkStyle) mark.extraWordmarkStyle = extraWordmarkStyle;
    }

    if (tech.icon) {
        mark.iconUrl = tech.icon;
        const extraIconStyle = parseExtraStyle(tech.extraIconStyle);
        if (extraIconStyle) mark.extraIconStyle = extraIconStyle;
    }

    if (tech.textColor) {
        mark.textColor = tech.textColor;
        const extraTextStyle = parseExtraStyle(tech.extraTextStyle);
        if (extraTextStyle) mark.extraTextStyle = extraTextStyle;
    }

    return {
        familiarity: tech.familiarity as 1 | 2 | 3 | 4 | 5,
        category: tech.categoryName,
        backgroundColor: tech.backgroundColor,
        name: tech.name,
        mark,
        ...(tech.noBorder && { noBorder: tech.noBorder }),
        description: tech.description,
        id: tech.id,
    };
}
